import { BaseApiService } from "./BaseApiService";
import { IPaginateApiResponse } from "model/Common/IPaginateApiResponse";
import { GROUP_CODE, PREFIX } from "utils/Const";
import { IPaginationRequest } from "model/Common/IPaginationRequest";
import {
  ICreateLocationRequest,
  ILocation,
  IUpdateLocationRequest,
} from "model/MasterData/ILocation";
import {
  ICreateZoneRequest,
  IUpdateZoneRequest,
  IZone,
} from "model/Zone/IZone";
import {
  ICreateProductRequest,
  IProduct,
  IUpdateProductRequest,
} from "model/Product/IProduct";
import { IApiResponse } from "model/Common/IApiResponse";
import { IProductCategory } from "model/MasterData/IProductCategory";
import { IUnitMeasure } from "model/MasterData/IUnitMeasure";
import { IZoneTemperature } from "model/MasterData/IZoneTemperature";
import {
  ICompany,
  ICreateCompanyRequest,
  IUpdateCompanyRequest,
} from "model/Company/ICompany";
import {
  ICreateUserRequest,
  IUpdateUserRequest,
  IUser,
} from "model/User/IUser";
import {
  ICreatePalletsRequest,
  ICreateWarehouseRequest,
  IDeleteByIdRequest,
  IGetByIdRequest,
  IPallet,
  IProductGroup,
  IUpdateWarehouseRequest,
  IWarehouse,
} from "model";

const prefix = PREFIX.masterData;

class MasterDataApiService extends BaseApiService {
  /**
   * @Warehouse api
   */
  public getAllWarehouses = (request: IPaginationRequest) =>
    this.get<IPaginateApiResponse<IWarehouse[]>>(
      `${prefix}/get-all-warehouses`,
      {
        params: request,
      }
    );
  public getWarehouseById = (request: IGetByIdRequest) =>
    this.get<IWarehouse>(`${prefix}/get-warehouse-by-id`, { params: request });
  public createWarehouse = (request: ICreateWarehouseRequest) =>
    this.post<ICreateWarehouseRequest, IWarehouse>(
      `${prefix}/create-warehouse`,
      request
    );
  public updateWarehouse = (request: IUpdateWarehouseRequest) =>
    this.put<IUpdateWarehouseRequest, IWarehouse>(
      `${prefix}/update-warehouse`,
      request
    );
  public deleteWarehouse = (request: IDeleteByIdRequest) =>
    this.delete<{ id: string }>(`${prefix}/delete-warehouse`, {
      params: request,
    });

  /**
   * @Product api
   */
  public getAllProducts = (request: IPaginationRequest) =>
    this.get<IPaginateApiResponse<IProduct[]>>(`${prefix}/get-all-product`, {
      params: request,
    });
  public getProductById = (request: IGetByIdRequest) =>
    this.get<IProduct>(`${prefix}/get-product-by-id`, { params: request });
  public createProduct = (request: ICreateProductRequest) =>
    this.post<ICreateProductRequest, IProduct>(
      `${prefix}/create-product`,
      request
    );
  public updateProduct = (request: IUpdateProductRequest) =>
    this.put<IUpdateProductRequest, IProduct>(
      `${prefix}/update-product`,
      request
    );
  public deleteProduct = (request: IDeleteByIdRequest) =>
    this.delete<{ id: string }>(`${prefix}/delete-product`, {
      params: request,
    });

  /**
   * @Pallet api
   */
  public getAllPallets = (request: IPaginationRequest) =>
    this.get<IPaginateApiResponse<IPallet[]>>(`${prefix}/get-all-pallets`, {
      params: request,
    });
  public createPallet = (request: ICreatePalletsRequest) =>
    this.post<ICreatePalletsRequest, IPallet>(
      `${prefix}/create-pallets`,
      request
    );

  /**
   * @Location api
   */
  public getAllLocations = (request: IPaginationRequest) =>
    this.get<IPaginateApiResponse<ILocation[]>>(`${prefix}/get-all-locations`, {
      params: request,
    });
  public getAllLocationsByProductLot = (request: IPaginationRequest) =>
    this.get<IPaginateApiResponse<ILocation[]>>(
      `${prefix}/get-all-locations-by-productlot`,
      {
        params: request,
      }
    );
  public createLocation = (request: ICreateLocationRequest) =>
    this.post<ICreateLocationRequest, ILocation>(
      `${prefix}/create-location`,
      request
    );
  public updateLocation = (request: IUpdateLocationRequest) =>
    this.put<IUpdateLocationRequest, ILocation>(
      `${prefix}/update-location`,
      request
    );
  public deleteLocation = (request: IDeleteByIdRequest) =>
    this.delete<{ id: string }>(`${prefix}/delete-location`, {
      params: request,
    });

  /**
   * @Zone api
   */
  public getAllZones = (request: IPaginationRequest) =>
    this.get<IPaginateApiResponse<IZone>>(`${prefix}/get-all-zones`, {
      params: request,
    });
  public createZone = (request: ICreateZoneRequest) =>
    this.post<ICreateZoneRequest, IZone>(`${prefix}/create-zone`, request);
  public updateZone = (request: IUpdateZoneRequest) =>
    this.put<IUpdateZoneRequest, IZone>(`${prefix}/update-zone`, request);
  public deleteZone = (request: IDeleteByIdRequest) =>
    this.delete<{ id: string }>(`${prefix}/delete-zone`, {
      params: request,
    });

  /**
   * @Company api
   */
  public getAllCompanies = (request: IPaginationRequest) =>
    this.get<IPaginateApiResponse<IZone>>(`${prefix}/get-all-companies`, {
      params: request,
    });
  public getCompanyById = (request: IGetByIdRequest) =>
    this.get<IProduct>(`${prefix}/get-company-by-id`, { params: request });
  public createCompany = (request: ICreateCompanyRequest) =>
    this.post<ICreateCompanyRequest, ICompany>(
      `${prefix}/create-company`,
      request
    );
  public updateCompany = (request: IUpdateCompanyRequest) =>
    this.put<IUpdateCompanyRequest, ICompany>(
      `${prefix}/update-company`,
      request
    );
  public deleteCompany = (request: IDeleteByIdRequest) =>
    this.delete<{ id: string }>(`${prefix}/delete-company`, {
      params: request,
    });

  /**
   * @USER api
   */
  public getAllUsers = (request: IPaginationRequest) =>
    this.get<IPaginateApiResponse<IUser>>(`${prefix}/get-all-users`, {
      params: request,
    });
  public getUserById = (request: IGetByIdRequest) =>
    this.get<IProduct>(`${prefix}/get-user-by-id`, { params: request });
  public createUser = (request: ICreateUserRequest) =>
    this.post<ICreateUserRequest, IUser>(`${prefix}/create-user`, request);
  public updateUser = (request: IUpdateUserRequest) =>
    this.put<IUpdateUserRequest, IUser>(`${prefix}/update-user`, request);
  public deleteUser = (request: IDeleteByIdRequest) =>
    this.delete<{ id: string }>(`${prefix}/delete-user`, {
      params: request,
    });

  /**
   * @Common
   */
  public getProductCategories = () =>
    this.get<IApiResponse<IProductCategory[]>>(
      `${prefix}/get-product-categories`
    );
  public getUnitMeasure = () =>
    this.get<IApiResponse<IUnitMeasure[]>>(`${prefix}/get-unit-measure`);
  public getZoneTemperature = () =>
    this.get<IApiResponse<IZoneTemperature[]>>(
      `${prefix}/get-references-by-group-code`,
      {
        params: { groupCode: GROUP_CODE.zoneTemperature },
      }
    );

  public getAvailableQRCodeBank = () =>
    this.get<IApiResponse<any>>(`${prefix}/get-available-qrcode`);
  public getAvailableCartonBank = () =>
    this.get<IApiResponse<any>>(`${prefix}/get-available-carton-code`);
  /**
   *
   * @ProductGroup Api
   */
  public getAllProductGroup = (request: IPaginationRequest) =>
    this.get<IProductGroup[]>(`${prefix}/get-product-groups`, {
      params: request,
    });
  public getProductGroupById = (id: string) =>
    this.get<IProductGroup>(`${prefix}/get-product-group-by-id`, {
      params: { id: id },
    });
  public addProductGroup = (request: Partial<IProductGroup>) =>
    this.post<Partial<IProductGroup>, IProductGroup>(
      `${prefix}/create-product-group`,
      request
    );
  public editProductGroup = (request: IProductGroup) =>
    this.put<IProductGroup, IProductGroup>(
      `${prefix}/update-product-group`,
      request
    );
  public deleteProductGroup = (id: string) =>
    this.delete<string>(`${prefix}/delete-product-group`, {
      params: { id: id },
    });
}

export default new MasterDataApiService(true);
